/**
 * Set while a session teardown is under way, so in-flight requests that each land on a
 * 401 don't each start their own.
 */
export const SessionEnding = {
    inProgress: false
};

// How long to keep de-duplicating concurrent teardowns before assuming the navigation
// never happened. Long enough to cover the requests that all fail together; short enough
// that a member left behind can still be signed out on the next 401.
const ABORTED_NAVIGATION_RESET_MS = 10000;

// A back/forward-cache restore brings this module's state back with the page, so without
// this the flag would still be set on a page that never actually went anywhere - leaving
// it unable to ever end its session again.
if (typeof window !== "undefined") {
    window.addEventListener("pageshow", (event: PageTransitionEvent) => {
        if (event.persisted) {
            SessionEnding.inProgress = false;
        }
    });
}

/**
 * Ends the session by sending the member through the server log out route.
 *
 * A full page navigation rather than an in-SPA teardown: the server route owns the sign
 * out, and reloading discards the whole redux store so the previous member's cached data
 * cannot leak to whoever signs in next.
 *
 * The server decides what to tell the log in page - see SessionEndReasonCookie - because
 * a reason this end could ask for would be a reason a crafted link could ask for too.
 *
 * Returns false when a teardown is already under way.
 */
export const endSessionThroughLogOut = (): boolean => {
    if (SessionEnding.inProgress) {
        return false;
    }

    SessionEnding.inProgress = true;

    // ?ended=1 marks this as an involuntary teardown (idle timeout, rejected refresh, or a
    // password change), so the log in page can say the session ended. A member who chose to
    // sign out reaches /logout without it - see HomeController.LogOut - and gets no message.
    window.location.assign("/logout?ended=1");

    // A blocked navigation - e.g. a beforeunload prompt the member answers with "stay" -
    // aborts the log out but fires no event we can hook. Without a reset the flag would be
    // stuck true and every later 401 would no-op, stranding the member in exactly the
    // broken authenticated state this is meant to end. Clear it once we can tell the page
    // is still here so the next failure can try again; a successful navigation unloads the
    // page first and this never runs.
    window.setTimeout(() => {
        SessionEnding.inProgress = false;
    }, ABORTED_NAVIGATION_RESET_MS);

    return true;
};
